import { useState, useEffect, useCallback, useRef } from "react";
import {
  OtStepperProps, DetalleOT, SeguimientoNota, EvidenciaImg,
  CustomGarage, Solicitud, Causa, Appointment, CatalogCategory,
  CatalogSubcategory, CatalogDetail, Role, AprobacionOtContext,
} from "../types/ot.types";
import { OtService } from "../services/OtService";
import {
  buildOtFechasCadena,
  countEvidenciasCierre,
  getOtFechaGapWarnings,
  validateOtFecha,
  type OtFechaCampo,
  type OtFechasCadena,
} from "../utils/otDateValidation";
import {
  EVALUACION_TALLER_DEFAULT,
  isEvaluacionTallerCompleta,
  normalizeEvaluacionTaller,
  type EvaluacionTallerState,
} from "../utils/otEvaluacionTaller";
import type { ConfirmModalProps } from "../../../shared/components/ConfirmModal";

export const STEPS_CONFIG = [
  { id: 1, label: "Agendar cita", icon: "fa-calendar-check" },
  { id: 2, label: "Ingresar bien", icon: "fa-user-check" },
  { id: 3, label: "Carga de diagnóstico", icon: "fa-stethoscope" },
  { id: 4, label: "Evidencias de reparación", icon: "fa-camera-retro" },
  { id: 5, label: "Aprobación de servicio", icon: "fa-file-invoice-dollar" },
  { id: 6, label: "Aprobación administrativa", icon: "fa-file-signature" },
  { id: 8, label: "Verificación de pago", icon: "fa-file-invoice-dollar" },
  { id: 9, label: "Entrega y Cierre", icon: "fa-check-circle" },
];

export const useOtStepper = (props: OtStepperProps) => {
  const isAdicional = props.tipoOrden === "adicional";
  const stepsVisibles = isAdicional ? STEPS_CONFIG.filter((s) => s.id >= 3) : STEPS_CONFIG;

  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(true);
  const [activeStep, setActiveStep] = useState<number>(isAdicional ? 3 : 1);
  const [maxReachedStep, setMaxReachedStep] = useState<number>(isAdicional ? 3 : 1);
  const [sysadminEditMode, setSysadminEditMode] = useState<boolean>(false);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ type: "success" | "danger" | "warning"; message: string; } | null>(null);

  const [detalleSolicitud, setDetalleSolicitud] = useState<Solicitud>();
  const [detalleOt, setDetalleOt] = useState<any>();

  const [causas, setCausas] = useState<Causa[]>([]);
  const [role, setRole] = useState<Role>();
  const [aprobacionOt, setAprobacionOt] = useState<AprobacionOtContext | null>(null);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [subcategoriesCache, setSubcategoriesCache] = useState<Record<string, CatalogSubcategory[]>>({});
  const [detailsCache, setDetailsCache] = useState<Record<string, CatalogDetail[]>>({});

  const [customGarages, setCustomGarages] = useState<CustomGarage[]>([]);
  const [usedCustomGarages, setUsedCustomGarages] = useState<CustomGarage[]>([]);
  const [selectedCustomGarage, setSelectedCustomGarage] = useState("");
  const [selectedGarage, setSelectedGarage] = useState<CustomGarage>();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [stepOneFormAppointment, setStepOneFormAppointment] = useState("");
  const [archivosRequerimiento, setArchivosRequerimiento] = useState<EvidenciaImg[]>([]);
  const [stepTwoFormAppointment, setStepTwoFormAppointment] = useState("");
  const [fechaEntregaFormAppointment, setFechaEntregaFormAppointment] = useState("");
  const [fechaTerminacionFormAppointment, setFechaTerminacionFormAppointment] = useState("");
  const [fechaEstimadaEntrega, setFechaEstimadaEntrega] = useState<string>("");
  const [estatusFirmaDigital, setEstatusFirmaDigital] = useState<"pendiente" | "firmado" | "cancelado">("firmado");

  const [detallesOT, setDetallesOT] = useState<DetalleOT[]>([{
    id: Date.now(), clave_orden_trabajo_detalle: 0, categoria: "", subcategoria: "", subcategoriadetalle: "",
    observaciones: "", pago_por_cliente: false, causa: "", cantidad: 1, garantia: "No", precio_unitario_sin_iva: "",
    porcentaje_iva: 16, porcentaje_isr: 0, aprobacion_cliente: "pendiente", aprobacion_admin: "pendiente", evidencia_omitida: false,
    categoria_label: "", subcategoria_label: "", subcategoria_detalle_label: "",
  }]);

  const [archivoCotizacion, setArchivoCotizacion] = useState<EvidenciaImg | undefined>();
  const [imagenesIngreso, setImagenesIngreso] = useState<EvidenciaImg[]>([]);
  const [imagenesPago, setImagenesPago] = useState<EvidenciaImg[]>([]);
  const [imagenesEntrega, setImagenesEntrega] = useState<EvidenciaImg[]>([]);
  const [evidenciasReparacion, setEvidenciasReparacion] = useState<Record<number, EvidenciaImg[]>>({});
  const [evidenciasCierre, setEvidenciasCierre] = useState<Record<number, EvidenciaImg[]>>({});
  const [excepcionesEvidencia, setExcepcionesEvidencia] = useState<number[]>([]);
  const [fechaAlta, setFechaAlta] = useState<string>("");
  const [showModalEvidenciasCierre, setShowModalEvidenciasCierre] = useState(false);
  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<
    Omit<ConfirmModalProps, "show" | "onConfirm" | "onCancel"> & { show: boolean }
  >({
    show: false,
    title: "",
    messages: [],
    confirmLabel: "Continuar",
    cancelLabel: "Cancelar",
    variant: "warning",
  });

  const [notasSeguimiento, setNotasSeguimiento] = useState<SeguimientoNota[]>([]);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [isSavingNota, setIsSavingNota] = useState(false);

  const [tipoPago, setTipoPago] = useState<"factura_relacionada" | "factura_pendiente" | "">("");
  const [folioFactura, setFolioFactura] = useState("");
  const [facturasDisponibles, setFacturasDisponibles] = useState<any[]>([]);
  const [evaluacionTaller, setEvaluacionTaller] = useState<EvaluacionTallerState>({
    ...EVALUACION_TALLER_DEFAULT,
  });

  const [voboGeneralUrl, setVoboGeneralUrl] = useState<string | undefined>();
  const [voboGeneralFile, setVoboGeneralFile] = useState<File | undefined>();

  const [proveedor, setProveedor] = useState<any>();
  const [ordenCompraPdfUrl, setOrdenCompraPdfUrl] = useState<string | null>(null);

  const parseArchivosEvidencias = (archivosCompletos: any[]) => {
    const inicio: Record<number, EvidenciaImg[]> = {};
    const cierre: Record<number, EvidenciaImg[]> = {};
    archivosCompletos
      .filter((a: any) => a.contexto === "STEP_EVIDENCIAS")
      .forEach((a: any) => {
        const detalleId = Number.parseInt(a.referencia_contexto);
        if (!Number.isNaN(detalleId)) {
          if (!inicio[detalleId]) inicio[detalleId] = [];
          inicio[detalleId].push(a);
        }
      });
    archivosCompletos
      .filter((a: any) => a.contexto === "STEP_EVIDENCIAS_CIERRE")
      .forEach((a: any) => {
        const detalleId = Number.parseInt(a.referencia_contexto);
        if (!Number.isNaN(detalleId)) {
          if (!cierre[detalleId]) cierre[detalleId] = [];
          cierre[detalleId].push(a);
        }
      });
    return { inicio, cierre };
  };

  const applyArchivosEvidencias = (archivosCompletos: any[]) => {
    const parsed = parseArchivosEvidencias(archivosCompletos);
    setEvidenciasReparacion(parsed.inicio);
    setEvidenciasCierre(parsed.cierre);
    return parsed;
  };

  const getOtFechasCadena = () =>
    buildOtFechasCadena({
      fechaAlta,
      fechaCita: stepOneFormAppointment,
      fechaRecepcion: stepTwoFormAppointment,
      fechaEstimadaEntrega: fechaEstimadaEntrega,
      fechaTerminacion: fechaTerminacionFormAppointment,
      fechaEntrega: fechaEntregaFormAppointment,
    });

  const askConfirm = useCallback(
    (
      options: Omit<ConfirmModalProps, "show" | "onConfirm" | "onCancel">,
    ): Promise<boolean> =>
      new Promise((resolve) => {
        confirmResolverRef.current = resolve;
        setConfirmDialog({
          show: true,
          title: options.title,
          messages: options.messages,
          message: options.message,
          confirmLabel: options.confirmLabel ?? "Continuar",
          cancelLabel: options.cancelLabel ?? "Cancelar",
          variant: options.variant ?? "warning",
        });
      }),
    [],
  );

  const resolveConfirmDialog = useCallback((confirmed: boolean) => {
    confirmResolverRef.current?.(confirmed);
    confirmResolverRef.current = null;
    setConfirmDialog((prev) => ({ ...prev, show: false }));
  }, []);

  const validarFechaPaso = async (
    campo: OtFechaCampo,
    valor: string,
    cadenaOverride?: OtFechasCadena,
  ): Promise<boolean> => {
    const cadena = cadenaOverride ?? getOtFechasCadena();
    const result = validateOtFecha(campo, valor, cadena);
    if (!result.valid) {
      setAlert({ type: "warning", message: result.message });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return false;
    }

    const warnings = getOtFechaGapWarnings(campo, valor, cadena);
    if (warnings.length > 0) {
      const confirmed = await askConfirm({
        title: "Revise las fechas capturadas",
        messages: warnings,
        confirmLabel: "Continuar de todos modos",
        cancelLabel: "Corregir fechas",
        variant: "warning",
      });
      if (!confirmed) {
        setAlert({
          type: "warning",
          message: "Revise las fechas capturadas antes de continuar.",
        });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return false;
      }
    }
    return true;
  };

  const loadInitialData = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      const cats = await OtService.fetchCategories();
      setCategories(cats);

      const detailRes = await OtService.fetchSolicitudDetalle(props.solicitud);
      if (detailRes?.data) {
        setDetalleSolicitud(detailRes.data.solicitud);
        setCausas(detailRes.data.causas || []);
        setRole(detailRes.data.role || []);
      }

      const notasBD = await OtService.fetchNotasSeguimiento(props.solicitud, props.orden);
      setNotasSeguimiento(notasBD);

      const detailOt = await OtService.fetchOtDetalle(props.orden);
      const orden = detailOt?.data?.orden;
      const prov = detailOt?.data?.proveedor;
      if (detailOt?.data?.aprobacion_ot) {
        setAprobacionOt(detailOt.data.aprobacion_ot);
      }
      console.log("detailOt asociado a la OT:", detailOt);
      if (prov) {
        console.log("Proveedor asociado a la OT:", prov);
        setProveedor(prov);
      }
      if (orden) {
        setDetalleOt(orden);
        if (orden.fecha_alta) setFechaAlta(orden.fecha_alta);
        if (orden.orden_compra_pdf_url) {
          setOrdenCompraPdfUrl(orden.orden_compra_pdf_url);
        }
        if (orden.fecha_recepcion_bien) setStepTwoFormAppointment(orden.fecha_recepcion_bien.replace(" ", "T").substring(0, 16));
        if (orden.fecha_estimada_entrega) setFechaEstimadaEntrega(orden.fecha_estimada_entrega.replace(" ", "T").substring(0, 16));
        if (orden.tipo_pago_servicio) setTipoPago(orden.tipo_pago_servicio);
        if (orden.folio_factura_coven) setFolioFactura(orden.folio_factura_coven);
        if (orden.fecha_entrega_bien) setFechaEntregaFormAppointment(orden.fecha_entrega_bien.replace(" ", "T").substring(0, 16));
        if (orden.fecha_terminacion) setFechaTerminacionFormAppointment(orden.fecha_terminacion.replace(" ", "T").substring(0, 16));
      }

      const archivosCompletos = await OtService.fetchArchivosOT(props.orden);
      setArchivosRequerimiento(archivosCompletos.filter((a: any) => a.contexto === "STEP_AGENDAR"));
      setImagenesIngreso(archivosCompletos.filter((a: any) => a.contexto === "STEP_INGRESO"));

      const voboGeneralBD = archivosCompletos.find((a: any) => a.contexto === "STEP_APROB_CLIENTE" && (!a.referencia_contexto || a.referencia_contexto === "GENERAL"));
      if (voboGeneralBD) {
        setVoboGeneralUrl(voboGeneralBD.url);
        setVoboGeneralFile({ name: voboGeneralBD.nombreOriginal, type: voboGeneralBD.tipo === "pdf" ? "application/pdf" : "image/*" } as any);
      }

      const imagenesEntregaFetched = archivosCompletos.filter((a: any) => a.contexto === "STEP_ENTREGA");
      setImagenesEntrega(imagenesEntregaFetched);

      const archivoCotizacionBD = archivosCompletos.find((a: any) => a.contexto === "STEP_DIAGNOSTICO");
      if (archivoCotizacionBD) setArchivoCotizacion({ ...archivoCotizacionBD, id: "bd_cargado" });

      const archivosVerificacionPagoTemp = archivosCompletos.filter((a: any) => a.contexto === "STEP_VERIFICACION_PAGO");
      setImagenesPago(archivosVerificacionPagoTemp);

      const archivosVoBoCliente = archivosCompletos.filter((a: any) => a.contexto === "STEP_APROB_CLIENTE");

      const { inicio: diccionarioEvidencias } = applyArchivosEvidencias(archivosCompletos);

      let appsFetched: Appointment[] = [];
      if (!isAdicional) {
        appsFetched = await OtService.fetchAppointments(props.orden);
        setAppointments(appsFetched);
        if (appsFetched.length > 0) {
          const citaConfirmada: any = appsFetched[0];
          setStepOneFormAppointment(citaConfirmada.fecha_cita);
          setSelectedGarage({
            clave_centro: citaConfirmada.clave_centro,
            clave_persona_proveedor: citaConfirmada.clave_persona_proveedor,
            nombre_completo: citaConfirmada.nombre_taller || "Taller Asignado",
            sucursal: "", phone: "", dias_atencion: "", distancia: "", fecha_alta: new Date(),
          });
        }
        // const garagesRes = await OtService.fetchCustomGarages(props.ciudad, props.bien, props.categoria, props.marca);
        // if (garagesRes?.data) {
        //   setCustomGarages(garagesRes.data.talleresCercanos || []);
        //   setUsedCustomGarages(garagesRes.data.talleresUsados || []);
        // }
        setCustomGarages([]);
        setUsedCustomGarages([]);
      }

      const conceptosRes = await OtService.fetchOtConceptos(props.orden);
      let mapped: DetalleOT[] = [];

      if (conceptosRes?.data?.conceptos && conceptosRes.data.conceptos.length > 0) {
        const dbConceptos = conceptosRes.data.conceptos;
        const categoriasExistentes = new Set(cats.map(c => c.id?.toString()));

        mapped = dbConceptos.map((c: any) => {
          const catIdStr = c.clave_categoria?.toString();

          if (catIdStr && !categoriasExistentes.has(catIdStr)) {
            cats.push({
              id: Number(c.clave_categoria),
              key_label: "LEGACY",
              nombre: c.categoria || `Dato Anterior`
            } as CatalogCategory);
            categoriasExistentes.add(catIdStr);
          }

          const voboDb = archivosVoBoCliente.find((a: any) => Number.parseInt(a.referencia_contexto) === c.clave_orden_trabajo_detalle);

          return {
            id: c.clave_orden_trabajo_detalle || Date.now() + Math.random(),
            clave_orden_trabajo_detalle: c.clave_orden_trabajo_detalle,
            categoria: c.clave_categoria?.toString() || "",
            categoria_label: c.categoria?.toString() || "",
            subcategoria: c.clave_subcategoria?.toString() || "",
            subcategoria_label: c.subcategoria?.toString() || "",
            subcategoriadetalle: c.clave_subcategoria_detalle?.toString() || "",
            subcategoria_detalle_label: c.titulo?.toString() || "",
            observaciones: c.observaciones || "",
            pago_por_cliente: c.garantia == 1,
            causa: c.clave_causa?.toString() || "",
            cantidad: c.cantidad || 1,
            garantia: c.garantia == 1 ? "Sí" : "No",
            precio_unitario_sin_iva: c.precio_unitario || 0,
            porcentaje_iva: c.porcentaje_iva || 16,
            porcentaje_isr: c.porcentaje_isr || 0,
            aprobacion_cliente: c.autorizado === 1 ? "aprobado" : c.autorizado === 0 ? "rechazado" : "pendiente",
            aprobacion_admin: c.clave_aprobacion === 1 ? "aprobado" : c.clave_aprobacion === 0 ? "rechazado" : "pendiente",
            evidencia_omitida: c.evidencia_omitida === 1,
            motivo_rechazo_cliente: c.motivo_rechazo_cliente,
            motivo_rechazo_admin: c.motivo_rechazo_admin,
            admin_exige_evidencia: c.admin_exige_evidencia == 1,
            voboUrl: voboDb ? voboDb.url : undefined,
            voboFile: voboDb ? ({ name: voboDb.nombreOriginal, type: voboDb.tipo === "pdf" ? "application/pdf" : "image/*" } as any) : undefined,
          };
        });

        setCategories([...cats]);
        setDetallesOT(mapped);
        setExcepcionesEvidencia(mapped.filter((c) => c.evidencia_omitida).map((c) => c.clave_orden_trabajo_detalle));

        const catsToFetch = [...new Set(mapped.map((c) => c.categoria).filter(Boolean))];
        let newSubCache = { ...subcategoriesCache };

        for (const cat of catsToFetch) {
          if (!newSubCache[cat]) {
            const subRes = await OtService.fetchSubcategories(cat);
            const subcatsEnOrden = mapped.filter(m => m.categoria === cat && m.subcategoria);
            subcatsEnOrden.forEach(sub => {
              const existe = subRes.find((s: any) => s.id?.toString() === sub.subcategoria || s.clave_subcategoria?.toString() === sub.subcategoria);
              if (!existe) {
                subRes.push({
                  id: Number(sub.subcategoria),
                  clave_subcategoria: Number(sub.subcategoria),
                  nombre: sub.subcategoria_label || "Dato Anterior",
                  subcategoria: sub.subcategoria_label || "Dato Anterior"
                } as unknown as CatalogSubcategory);
              }
            });
            newSubCache[cat] = subRes;
          }
        }
        setSubcategoriesCache(newSubCache);

        let newDetCache = { ...detailsCache };
        for (const c of mapped) {
          if (c.categoria && c.subcategoria && c.subcategoriadetalle) {
            const key = `${c.categoria}-${c.subcategoria}`;
            if (!newDetCache[key]) {
              const detRes = await OtService.fetchSubcategoryDetails(c.categoria, c.subcategoria);
              const existeDetalle = detRes.find((d: any) => d.id?.toString() === c.subcategoriadetalle || d.clave_subcategoria_detalle?.toString() === c.subcategoriadetalle);
              if (!existeDetalle) {
                detRes.push({
                  id: Number(c.subcategoriadetalle),
                  clave_subcategoria_detalle: Number(c.subcategoriadetalle),
                  nombre: c.subcategoria_detalle_label || "Dato Anterior",
                  titulo: c.subcategoria_detalle_label || "Dato Anterior"
                } as unknown as CatalogDetail);
              }
              newDetCache[key] = detRes;
            }
          }
        }
        setDetailsCache(newDetCache);
      }

      let estatusFirmaDigitalTemp: "pendiente" | "firmado" | "cancelado" = "firmado";
      if (orden?.tipo_pago_servicio && orden?.folio_factura_coven) {
        estatusFirmaDigitalTemp = "firmado";
        setEstatusFirmaDigital(estatusFirmaDigitalTemp);
      }

      if ((estatusFirmaDigitalTemp === "firmado" && archivosVerificacionPagoTemp.length > 0) || orden?.fecha_entrega_bien || imagenesEntregaFetched.length > 0) {
        const evaluacionResponse = await OtService.fetchEvaluacionTaller(props.orden);
        if (evaluacionResponse?.data?.reviews) {
          setEvaluacionTaller(
            normalizeEvaluacionTaller(evaluacionResponse.data.reviews),
          );
        }
      }

      let determinedStep = isAdicional ? 3 : 1;

      if (orden) {
        if (!isAdicional && appsFetched.length > 0) determinedStep = Math.max(determinedStep, 2);
        if (orden.fecha_recepcion_bien) determinedStep = Math.max(determinedStep, 3);
        if (mapped.length > 0 || orden.monto_cotizado > 0 || archivoCotizacionBD) determinedStep = Math.max(determinedStep, 4);

        if (mapped.length > 0) {
          const algunVoBoCliente = mapped.some(d => d.aprobacion_cliente !== "pendiente");
          const todosVoBoCliente = mapped.every(d => d.aprobacion_cliente !== "pendiente");

          const faltanEvidencias = mapped.some((detalle) => {
            const tieneArchivos = diccionarioEvidencias[detalle.clave_orden_trabajo_detalle]?.length > 0;
            return !tieneArchivos && !detalle.evidencia_omitida;
          });

          if (!faltanEvidencias || algunVoBoCliente) determinedStep = Math.max(determinedStep, 5);

          const algunVoBoAdmin = mapped.some(d => d.aprobacion_admin !== "pendiente");
          if (todosVoBoCliente || algunVoBoAdmin) determinedStep = Math.max(determinedStep, 6);

          const todosVoBoAdmin = mapped.every(d => d.aprobacion_admin !== "pendiente");
          if (todosVoBoAdmin) determinedStep = Math.max(determinedStep, 8);

          const isImmediatePayment = prov?.clave_periodo_pago == 1;
          if (determinedStep === 8 && !isImmediatePayment) {
            determinedStep = Math.max(determinedStep, 9);
          }
        }
        let informacionPendiente = Number(orden.rechazada_ejecutivo);
        if (informacionPendiente === 1) {
          determinedStep = 3;
        } else if (informacionPendiente === 2) {
          determinedStep = 4;
        } else {
          if (archivosVerificacionPagoTemp.length > 0 || imagenesEntregaFetched.length > 0) {
            determinedStep = Math.max(determinedStep, 9);
          }
          if (orden.fecha_entrega_bien) {
            determinedStep = 10;
          }
        }
      }

      setMaxReachedStep(determinedStep);
      setActiveStep(determinedStep === 10 ? 9 : determinedStep);

    } catch (error) {
      console.error(error);
      setAlert({ type: "danger", message: "Error al sincronizar con el servidor." });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      setIsLoadingInitial(false);
    }
  }, [props.solicitud, props.orden, props.ciudad, props.bien, props.categoria, props.marca, isAdicional]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const getStepKeyLabel = (stepId: number): string => {
    const mapper: Record<number, string> = {
      1: "STEP_AGENDAR", 2: "STEP_INGRESO", 3: "STEP_DIAGNOSTICO", 4: "STEP_EVIDENCIAS",
      5: "STEP_APROB_CLIENTE", 6: "STEP_APROB_ADMIN", 8: "STEP_VERIFICACION_PAGO", 9: "STEP_ENTREGA",
    };
    return mapper[stepId] || "LEGACY_GENERAL";
  };

  const validarPasoActual = async (): Promise<boolean> => {
    if (activeStep === 1 && !isAdicional) {
      if (!selectedGarage) {
        setAlert({ type: "warning", message: "Por favor, selecciona o busca un taller válido." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" }); return false;
      }
      if (!stepOneFormAppointment) {
        setAlert({ type: "warning", message: "Por favor, ingresa la fecha y hora de la cita." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" }); return false;
      }
      if (!(await validarFechaPaso("fecha_cita", stepOneFormAppointment))) return false;
    }
    if (activeStep === 2) {
      if (!stepTwoFormAppointment) {
        setAlert({ type: "warning", message: "Por favor, ingresa la fecha y hora de ingreso." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" }); return false;
      }
      if (!(await validarFechaPaso("fecha_recepcion", stepTwoFormAppointment))) return false;
    }
    if (activeStep === 3) {
      if (fechaEstimadaEntrega && !(await validarFechaPaso("fecha_estimada_entrega", fechaEstimadaEntrega))) return false;
    }
    if (activeStep === 9) {
      if (!fechaTerminacionFormAppointment) {
        setAlert({ type: "warning", message: "Por favor, ingresa la fecha de terminación de servicio." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" }); return false;
      }
      if (!fechaEntregaFormAppointment) {
        setAlert({ type: "warning", message: "Por favor, ingresa la fecha de entrega de unidad." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" }); return false;
      }
      if (imagenesEntrega.length === 0) {
        setAlert({ type: "warning", message: "Debe adjuntar evidencia del acta de entrega o fotografías." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return false;
      }
      if (!isEvaluacionTallerCompleta(evaluacionTaller)) {
        setAlert({ type: "warning", message: "Por favor, asigne una calificación a todos los rubros." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return false;
      }
      if (!(await validarFechaPaso("fecha_terminacion", fechaTerminacionFormAppointment))) return false;
      const cadenaConTerminacion = {
        ...getOtFechasCadena(),
        fecha_terminacion: fechaTerminacionFormAppointment,
      };
      if (!(await validarFechaPaso("fecha_entrega", fechaEntregaFormAppointment, cadenaConTerminacion))) {
        return false;
      }
    }
    return true;
  };

  const handleStepClick = (clickedStepId: number) => {
    if (clickedStepId > maxReachedStep) {
      setAlert({ type: "warning", message: `Debes completar los pasos anteriores para avanzar.` });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => setAlert(null), 4000);
      return;
    }
    setAlert(null);
    setActiveStep(clickedStepId);
  };

  const avanzarPaso = async () => {
    if (!(await validarPasoActual())) {
      setTimeout(() => setAlert(null), 5000);
      return;
    }
    if (activeStep >= 9 && maxReachedStep === 10) return;

    let auditReason = "";
    if (sysadminEditMode) {
      const reason = window.prompt("MODO SYSADMIN: Ingrese quién solicitó esta edición y el motivo para la auditoría:");
      if (!reason || reason.trim() === "") {
        setAlert({ type: "warning", message: "Edición cancelada. El motivo de auditoría es obligatorio." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      auditReason = reason.trim();
    }

    setIsProcessing(true);
    setAlert(null);

    try {
      const pasoActual = STEPS_CONFIG.find((s) => s.id === activeStep)?.label || "";

      if (activeStep === 1) {
        if (!detalleSolicitud || !selectedGarage || !stepOneFormAppointment) throw new Error("Datos internos incompletos.");
        const apps = await OtService.createAppointment(detalleSolicitud.clave_persona_cliente, props.orden, stepOneFormAppointment, selectedGarage.clave_persona_proveedor, selectedGarage.clave_centro, archivosRequerimiento);
        if (apps && apps.length > 0) {
          setAppointments(apps); setActiveStep(2); setMaxReachedStep((prev) => Math.max(prev, 2));
        } else {
          throw new Error("El servidor no devolvió la confirmación.");
        }
      } else if (activeStep === 2) {
        if (!detalleSolicitud || !stepTwoFormAppointment) throw new Error("Datos incompletos.");
        if (imagenesIngreso.length === 0) throw new Error("Es necesario cargar evidencia para el ingreso.");
        await OtService.createAppointmentEntry(detalleSolicitud.clave_persona_cliente, props.orden, stepTwoFormAppointment, imagenesIngreso);
        setActiveStep(3); setMaxReachedStep((prev) => Math.max(prev, 3));
      } else if (activeStep === 3) {
        const formCreateDiagnosis = document.getElementById("create-diagnosis") as HTMLFormElement;

        if (detalleSolicitud && archivoCotizacion && detallesOT.length > 0) {
          if (!fechaEstimadaEntrega) {
            setAlert({ type: "danger", message: "La fecha estimada de entrega es obligatoria." });
            document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
            setIsProcessing(false); return;
          }

          const hayConceptosIncompletos = detallesOT.some(d => !d.categoria || !d.subcategoria || !d.subcategoriadetalle || !d.causa || Number(d.cantidad) <= 0 || d.precio_unitario_sin_iva === "");
          if (hayConceptosIncompletos) {
            setAlert({ type: "danger", message: "Existen conceptos con información incompleta." });
            document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
            setIsProcessing(false); return;
          }

          if (formCreateDiagnosis && !formCreateDiagnosis.reportValidity()) {
            setIsProcessing(false); return;
          }

          const catPreventivo = categories.find((c) => c.key_label === "PREVENTIVOS");
          let confirmadoPreventivo = false;
          if (catPreventivo && detallesOT.length === 1 && String(detallesOT[0].categoria) === String(catPreventivo.id)) {
            const cant = Number.parseFloat(String(detallesOT[0].cantidad)) || 0;
            const prec = Number.parseFloat(String(detallesOT[0].precio_unitario_sin_iva)) || 0;
            const iva = (Number.parseFloat(String(detallesOT[0].porcentaje_iva)) || 0) / 100;
            const isr = (Number.parseFloat(String(detallesOT[0].porcentaje_isr || 0)) || 0) / 100;
            const sub = cant * prec;
            const montoPrev = sub + sub * iva - sub * isr;
            const baseRef = 2800;
            const msg = `Mantenimiento preventivo único por $${montoPrev.toLocaleString("es-MX", { minimumFractionDigits: 2 })}.\n\nSi el monto está dentro del umbral de preautorización (~$${baseRef.toLocaleString("es-MX")} ±10%), la OT no requerirá aprobación operativa.\nSi está fuera de tolerancia, se registrará una alerta para Admin Talleres.\n\n¿Confirma que el monto de la cotización es correcto?`;
            if (!globalThis.confirm(msg)) {
              setIsProcessing(false);
              return;
            }
            confirmadoPreventivo = true;
          }

          await OtService.createDignosis(detalleSolicitud.clave_persona_cliente, props.orden, archivoCotizacion, detallesOT, fechaEstimadaEntrega, confirmadoPreventivo);

          await loadInitialData();

        } else {
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setAlert({ type: "danger", message: "La cotización debe subirse en PDF y al menos un concepto debe existir." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }
      } else if (activeStep === 4) {
        if (!detalleSolicitud) throw new Error("Datos incompletos.");
        const excepcionesEnriquecidas: Array<{ idReal: number; descripcion: string }> = [];
        const conceptosFaltantes = detallesOT.filter((detalle) => {
          const idReal = detalle.clave_orden_trabajo_detalle;
          const tieneArchivos = evidenciasReparacion[idReal] && evidenciasReparacion[idReal].length > 0;
          const estaOmitido = excepcionesEvidencia.includes(idReal);
          if (estaOmitido) {
            excepcionesEnriquecidas.push({ idReal: idReal, descripcion: detalle.observaciones ? detalle.observaciones.substring(0, 30) + "..." : `Concepto sin descripción`, });
          }
          return !tieneArchivos && !estaOmitido;
        });

        if (conceptosFaltantes.length > 0) {
          setAlert({ type: "warning", message: "Debes subir evidencia para todos los conceptos." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        await OtService.saveEvidenciasReparacion(detalleSolicitud.clave_persona_cliente, props.orden, evidenciasReparacion, evidenciasCierre, excepcionesEnriquecidas);
        setActiveStep(5); setMaxReachedStep((prev) => Math.max(prev, 5));
      } else if (activeStep === 5 && detalleSolicitud) {
        const esPreautorizacion =
          aprobacionOt?.etapa_actual === "PREAUTORIZACION_PREVENTIVO" ||
          aprobacionOt?.etapa_actual === "BYPASS_PREVENTIVO";
        const puedeAprobar = esPreautorizacion
          || !!aprobacionOt?.puede_actuar
          || !!aprobacionOt?.puede_aprobar
          || role?.role === "SYSADMIN"
          || role?.key_role === "SYS_ADMIN";
        if (!puedeAprobar) {
          setAlert({
            type: "danger",
            message: aprobacionOt?.motivo_solo_lectura ?? "Acceso denegado: no tiene permiso para registrar la aprobación de esta orden."
          });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false);
          return;
        }
        const todosRechazados = detallesOT.length > 0 && detallesOT.every((d) => d.aprobacion_cliente === "rechazado");
        const tienePendientes = detallesOT.some((d) => d.aprobacion_cliente === "pendiente");

        const tieneVoboGeneral = !!voboGeneralUrl;
        const todosConceptosTienenVobo = detallesOT.filter(d => d.aprobacion_cliente === "aprobado").every(d => !!d.voboUrl);
        const faltanVobos = !tieneVoboGeneral && !todosConceptosTienenVobo;

        if (todosRechazados) {
          setAlert({ type: "danger", message: "Todos los conceptos han sido rechazados. Use el botón 'Rechazar a Ejecutivo' para devolver la orden." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        if (tienePendientes) {
          setAlert({ type: "danger", message: "Debe aprobar o rechazar TODOS los conceptos antes de avanzar." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        if (faltanVobos) {
          setAlert({ type: "danger", message: "Se requiere el VoBo del cliente (General o por conceptos) antes de avanzar." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        const notaEtapa = globalThis.prompt("Nota de la etapa (opcional):") || "";
        const aprobacionRes = await OtService.saveAprobacionesCliente(detalleSolicitud.clave_persona_cliente, props.orden, detallesOT, notaEtapa);
        if (aprobacionRes?.data?.orden_compra_pdf_url) {
          setOrdenCompraPdfUrl(aprobacionRes.data.orden_compra_pdf_url);
        }

        const aprobacionCompleta = !!(
          aprobacionRes?.data?.aprobacion_completa ?? aprobacionRes?.data?.cascada_completa
        );
        if (!aprobacionCompleta) {
          setAlert({
            type: "success",
            message: aprobacionRes?.message || "Nivel registrado. Pendiente el siguiente nivel de aprobación por umbral de monto.",
          });
          await loadInitialData();
          setIsProcessing(false);
          return;
        }

        const isImmediatePayment = proveedor.clave_periodo_pago == 1;

        if (!isImmediatePayment) {
          setActiveStep(9);
          setMaxReachedStep(9);
        } else {
          setActiveStep(6);
          setMaxReachedStep(6);
        }
      } else if (activeStep === 6) {
        if (role?.role !== "Super Admin" && role?.role !== "Super Administrador" && role?.role !== "Administrador Contraloria" && role?.role !== "SYSADMIN") {
          setAlert({ type: "danger", message: "Acceso denegado: Solo el Administrador Contraloría puede registrar la aprobación administrativa." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false);
          return;
        }

        const tienePendientesAdmin = detallesOT.some((d) => d.aprobacion_admin === "pendiente");
        const todosRechazadosAdmin = detallesOT.length > 0 && detallesOT.every((d) => d.aprobacion_admin === "rechazado");

        if (tienePendientesAdmin) {
          setAlert({ type: "danger", message: "Debe validar administrativamente TODOS los conceptos." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        if (todosRechazadosAdmin) {
          setAlert({ type: "danger", message: "Todos los conceptos fueron rechazados. Use el botón 'Rechazar a Ejecutivo' para devolver la orden." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        await OtService.saveAprobacionAdmin(detalleSolicitud!.clave_persona_cliente, props.orden, detallesOT);

        const isImmediatePayment = proveedor.clave_periodo_pago == 1;

        if (!isImmediatePayment) {
          setActiveStep(9);
          setMaxReachedStep(9);
        } else {
          setActiveStep(8);
          setMaxReachedStep(8);
        }

      } else if (activeStep === 8) {
        const isImmediatePayment = proveedor?.clave_periodo_pago == 1;

        if (isImmediatePayment && imagenesPago.length === 0) {
          setAlert({ type: "warning", message: "Para talleres de pago inmediato, debe adjuntar al menos un comprobante." });
          document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
          setIsProcessing(false); return;
        }

        if (imagenesPago.length > 0) {
          await OtService.savePagoServicio(props.orden, imagenesPago);
        }
        setMaxReachedStep(Math.max(maxReachedStep, 9));
      } else if (activeStep === 9) {
        const totalCierre = countEvidenciasCierre(evidenciasCierre);
        let sinEvidenciaCierre = totalCierre === 0;
        if (sinEvidenciaCierre) {
          const confirmar = await askConfirm({
            title: "Sin evidencia de cierre de reparación",
            message:
              "No ha cargado evidencia de finalización de reparación. ¿Desea cerrar la orden de todos modos? Quedará registrado en la bitácora.",
            confirmLabel: "Cerrar de todos modos",
            cancelLabel: "Volver a cargar evidencia",
            variant: "warning",
          });
          if (!confirmar) {
            setIsProcessing(false);
            return;
          }
        }

        await OtService.saveEntregaCierre(
          props.orden,
          imagenesEntrega,
          fechaEntregaFormAppointment,
          fechaTerminacionFormAppointment,
          normalizeEvaluacionTaller(evaluacionTaller),
          evidenciasCierre,
          sinEvidenciaCierre,
        );
        setMaxReachedStep(10);
      }

      if (auditReason) {
        await OtService.saveNotaSeguimiento(props.solicitud, props.orden, `[EDICIÓN SYSADMIN]: ${auditReason}`, getStepKeyLabel(activeStep));
      }

      const archivosCompletos = await OtService.fetchArchivosOT(props.orden);
      applyArchivosEvidencias(archivosCompletos);

      setAlert({ type: "success", message: `El paso "${pasoActual}" se completó correctamente.` });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error: any) {
      console.error(error);
      setAlert({ type: "danger", message: error.message || "Error de comunicación con el servidor." });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      const notasActualizadas = await OtService.fetchNotasSeguimiento(props.solicitud, props.orden);
      setNotasSeguimiento(notasActualizadas);
      setIsProcessing(false);
      setTimeout(() => setAlert(null), 4000);
    }
  };

  const guardarNotaSeguimiento = async () => {
    if (!nuevoComentario.trim() || !detalleSolicitud) return;
    setIsSavingNota(true);
    try {
      const exito = await OtService.saveNotaSeguimiento(props.solicitud, props.orden, nuevoComentario.trim(), getStepKeyLabel(activeStep));
      if (exito) {
        const notasActualizadas = await OtService.fetchNotasSeguimiento(props.solicitud, props.orden);
        setNotasSeguimiento(notasActualizadas); setNuevoComentario("");
      } else {
        setAlert({ type: "danger", message: "No se pudo guardar la nota en el sistema." });
        document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (error) {
      setAlert({ type: "danger", message: "Error al comunicarse con el servidor." });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      setIsSavingNota(false);
    }
  };

  const rechazarOrdenAEjecutivo = async (motivo: string, tipoRechazo: number) => {
    if (!motivo.trim() || !detalleSolicitud) return;
    setIsProcessing(true);
    try {
      await OtService.rechazarOtEjecutivo(props.orden, motivo, tipoRechazo);

      const destino = tipoRechazo === 1 ? "Diagnóstico y Costos (Paso 3)" : "Solo Evidencias (Paso 4)";
      await OtService.saveNotaSeguimiento(
        props.solicitud,
        props.orden,
        `[RECHAZO A EJECUTIVO]: La orden fue devuelta a ${destino}. Motivo: ${motivo.trim()}`,
        getStepKeyLabel(activeStep)
      );

      setAlert({ type: "success", message: `La Orden fue devuelta a ${destino}.` });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      loadInitialData();
    } catch (error: any) {
      setAlert({ type: "danger", message: error.message || "Error al rechazar la orden." });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      setIsProcessing(false);
    }
  };

  const uploadVoBoDocument = async (detalleId: number | undefined, file: File): Promise<boolean> => {
    try {
      const serverUrl = await OtService.uploadVoBoCliente(props.orden, detalleId, file);
      if (detalleId) {
        updateDetalle(detalleId, "voboFile", file);
        updateDetalle(detalleId, "voboUrl", serverUrl);
      } else {
        setVoboGeneralFile(file);
        setVoboGeneralUrl(serverUrl);
      }
      setAlert({ type: "success", message: `El VoBo ha sido guardado exitosamente.` });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    } catch (error: any) {
      setAlert({ type: "danger", message: error.message || "No se pudo subir el archivo VoBo." });
      document.getElementById("react-ot-stepper")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return false;
    }
  };

  const addNewConcept = () => {
    setDetallesOT((prev) => [...prev, {
      id: Date.now(), clave_orden_trabajo_detalle: 0, categoria: "", subcategoria: "", subcategoriadetalle: "",
      observaciones: "", pago_por_cliente: false, causa: "", cantidad: 1, garantia: "No", precio_unitario_sin_iva: "",
      porcentaje_iva: 16, porcentaje_isr: 0, aprobacion_cliente: "pendiente", aprobacion_admin: "pendiente", evidencia_omitida: false,
      categoria_label: "", subcategoria_label: "", subcategoria_detalle_label: "",
    }]);
  };

  const updateDetalle = (id: number, field: keyof DetalleOT, value: any) => {
    setDetallesOT((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const deleteDetalle = (detalleId: number) => {
    setDetallesOT((prev) => prev.filter((d) => d.id !== detalleId));
  };

  const fillSubcategory = async (detalleId: number, idCategoria: string) => {
    setDetallesOT((prev) => prev.map((d) => d.id === detalleId ? { ...d, categoria: idCategoria, subcategoria: "", subcategoriadetalle: "" } : d));
    if (idCategoria && !subcategoriesCache[idCategoria]) {
      const response = await OtService.fetchSubcategories(idCategoria);
      setSubcategoriesCache((prev) => ({ ...prev, [idCategoria]: response }));
    }
  };

  const fillDetailSubcategory = async (detalleId: number, idCategoria: string, idSubcategoria: string) => {
    setDetallesOT((prev) => prev.map((d) => d.id === detalleId ? { ...d, subcategoria: idSubcategoria, subcategoriadetalle: "" } : d));
    const cacheKey = `${idCategoria}-${idSubcategoria}`;
    if (idSubcategoria && !detailsCache[cacheKey]) {
      const response = await OtService.fetchSubcategoryDetails(idCategoria, idSubcategoria);
      setDetailsCache((prev) => ({ ...prev, [cacheKey]: response }));
    }
  };

  const tieneOrdenCompra = !!ordenCompraPdfUrl;

  const tieneEvidenciaCierrePendiente = countEvidenciasCierre(evidenciasCierre) === 0;
  const otFechasCadena = getOtFechasCadena();

  return {
    state: {
      isLoadingInitial, activeStep, maxReachedStep, isProcessing, alert, stepsVisibles, isAdicional, detalleSolicitud,
      detalleOt, causas, categories, subcategoriesCache, detailsCache, customGarages, usedCustomGarages, selectedCustomGarage,
      selectedGarage, appointments, stepOneFormAppointment, stepTwoFormAppointment, fechaEstimadaEntrega, archivosRequerimiento,
      detallesOT, archivoCotizacion, imagenesIngreso, imagenesPago, imagenesEntrega, evidenciasReparacion, evidenciasCierre,
      excepcionesEvidencia, fechaAlta, otFechasCadena, tieneEvidenciaCierrePendiente, showModalEvidenciasCierre, confirmDialog,
      notasSeguimiento, nuevoComentario, isSavingNota, evaluacionTaller, tipoPago, folioFactura, facturasDisponibles, estatusFirmaDigital,
      fechaEntregaFormAppointment, fechaTerminacionFormAppointment, role, aprobacionOt, voboGeneralUrl, voboGeneralFile, sysadminEditMode, proveedor,
      ordenCompraPdfUrl, tieneOrdenCompra,
    },
    actions: {
      setActiveStep, setSelectedCustomGarage, setSelectedGarage, setStepOneFormAppointment, setStepTwoFormAppointment, setFechaEstimadaEntrega,
      setArchivosRequerimiento, setArchivoCotizacion, setImagenesIngreso, setImagenesPago, setImagenesEntrega, setEvaluacionTaller,
      setEvidenciasReparacion, setEvidenciasCierre, setExcepcionesEvidencia, setShowModalEvidenciasCierre, resolveConfirmDialog, setNuevoComentario, handleStepClick, avanzarPaso, guardarNotaSeguimiento, addNewConcept,
      updateDetalle, deleteDetalle, fillSubcategory, fillDetailSubcategory, refreshData: loadInitialData, setTipoPago, setFolioFactura,
      setFacturasDisponibles, setEstatusFirmaDigital, setFechaEntregaFormAppointment, setFechaTerminacionFormAppointment, setRole, uploadVoBoDocument, rechazarOrdenAEjecutivo, setSysadminEditMode
    },
  };
};